import React, {
  useCallback,
  useMemo,
} from 'react';
import {
  ServiceFacilityDTO,
} from 'dtos';
import Select, {
  Props as SelectProps,
} from 'core/components/Select';
import compact from 'lodash/compact';
import Spinner from 'pages/Dashboard/components/Spinner';
import NPIInfo from 'pages/Dashboard/components/NPIInfo';
import {
  getAddressString,
} from 'utils/string';
import {
  useServiceFacilities,
} from 'views/EMR/stores/serviceFacility';
import {
  notEmptyString,
} from '@populateui/utils';
import isNil from 'lodash/isNil';

type Props = Pick<
  SelectProps<ServiceFacilityDTO>,
  'label'
  | 'labelClasses'
  | 'required'
  | 'muiClasses'
  | 'disabled'
  | 'className'
  | 'error'
  | 'showErrorText'
> & {
  isLoading?: boolean;
  value?: ServiceFacilityDTO;
  onChange: (val: ServiceFacilityDTO) => void;
  includeClaimsInfo?: boolean;
  additionalOptions?: ServiceFacilityDTO[];
};

type AddressLabelProps = ServiceFacilityDTO & {
  includeAddress?: boolean;
  includeNPIInfo?: boolean;
  includePOSInfo?: boolean;
};

function AddressLabel({
  address,
  nickname,
  npi,
  defaultPOS,
  isInternal,
  isPlaceholder,
  includeAddress = false,
  includeNPIInfo = false,
  includePOSInfo = false,
}: AddressLabelProps) {
  const addressString = getAddressString(address);

  return (
    <div className="flex flex-col w-full">
      <span className="w-full truncate font-semibold">{nickname}</span>
      {includeAddress && !isNil(address) && <small>{isPlaceholder ? 'Placeholder' : addressString ?? '--'}</small>}
      {includeNPIInfo && isInternal
        ? (
          <small>
            Internal Facility
          </small>
        )
        : includeNPIInfo && <NPIInfo {...(npi ?? {})} />}
      {includePOSInfo && notEmptyString(defaultPOS) && (
        <small>{compact(['POS', defaultPOS]).join(' • ')}</small>
      )}
    </div>
  );
}

export default function ServiceFacilitySelect({
  value,
  isLoading,
  onChange,
  includeClaimsInfo = false,
  disabled = false,
  additionalOptions = [],
  ...props
}: Props) {
  const serviceFacilitiesOptions = useServiceFacilities();
  const serviceFacilities = useMemo(() => ([
    ...additionalOptions,
    ...(serviceFacilitiesOptions ?? []),
  ]), [additionalOptions, serviceFacilitiesOptions]);

  const selectedValue = useMemo(
    () =>
      serviceFacilities.find(
        (facility) =>
          value?.serviceFacilityId === facility.serviceFacilityId,
      ),
    [value, serviceFacilities],
  );

  const getLabel = useCallback((option: ServiceFacilityDTO) => (
    <AddressLabel
      {...option}
      includeAddress
      includeNPIInfo={includeClaimsInfo}
      includePOSInfo={includeClaimsInfo}
    />
  ), [includeClaimsInfo]);

  return (
    <Select<ServiceFacilityDTO>
      {...props}
      value={selectedValue}
      options={serviceFacilities}
      placeholder="Select location"
      getLabel={getLabel}
      getValue={({ serviceFacilityId }) => serviceFacilityId ?? 0}
      onChange={(value) => { onChange(value as ServiceFacilityDTO); }}
      renderValue={() => (
        isLoading
          ? (
            <p className="flex items-center gap-2">
              <Spinner />
              Updating...
            </p>
          )
          : (
            <AddressLabel
              {...selectedValue}
              includeAddress={includeClaimsInfo}
              includeNPIInfo={includeClaimsInfo}
              includePOSInfo={includeClaimsInfo}
            />
          ))}
      disabled={disabled || isLoading}
    />
  );
}
